import {useCallback, useEffect, useState} from 'react';
import {
    PhoneVerificationState,
    PhoneVerificationStatus,
    PhoneVerificationSendCodeResult,
    PhoneVerificationStateValue,
} from '@target-web/leadads-form-view';
import {
    PhoneVerificationCheckCode,
    PhoneVerificationUserInfo,
    VerificationPhoneCodeResponse,
} from 'api/types/PhoneVerification';
import {attemptVerifyCodeRequest, sendVerifyCodeRequest, checkPhoneVerificationRequest} from 'api/services/leads';
import {useUserInfo} from 'utils/userInfoContext';
import {useSearchParams} from 'utils/searchParamsContext';
import useAnalytics from './useAnalytics';
import {isDev, VERIFICATION_PHONE_SEND_LIMIT_IN_MS} from 'utils/constants';
import {getUserPhoneDataDecodeParams} from 'utils/utils';

type AntifraudTimers = {
    verify: ReturnType<typeof setTimeout> | null;
    send: ReturnType<typeof setTimeout> | null;
    attempt: ReturnType<typeof setTimeout> | null;
};

type PrevPhoneState = {
    phone: string;
    state: PhoneVerificationStatus;
};

function usePhoneVerificationController(codeLength: number) {
    const defaultAntifraudTimers: AntifraudTimers = {
        verify: null,
        send: null,
        attempt: null,
    };
    const [prevPhoneState, setPrevPhoneState] = useState<PrevPhoneState | undefined>();
    const userInfo = useUserInfo();
    const {formDataHash} = useSearchParams();
    const {phoneBannedAnalyticEvent} = useAnalytics();
    const [phoneVerificationState, setPhoneVerificationState] = useState<PhoneVerificationState>({
        status: PhoneVerificationStatus.unknown,
        attempt: {timestamp: undefined},
        antifraudExperies: {
            verify: null,
            send: null,
            attempt: null,
        },
    });

    const [antifraudTimers, setAntifraudTimers] = useState<AntifraudTimers>(defaultAntifraudTimers);

    const setPhoneForCheckingVerificationStatus = useCallback(
        (phone: PhoneVerificationStateValue) => {
            if (!phone.value) return;

            setPhoneVerificationState((prev) => ({
                ...prev,
                phone,
                status: PhoneVerificationStatus.verificationProcessing,
            }));
        },
        [setPhoneVerificationState],
    );

    const setCodeForVerify = useCallback(
        (code: string) => {
            if (code.length !== codeLength) return false;
            setPhoneVerificationState((prev) => ({
                ...prev,
                errors: undefined,
                code: {value: code, hash: `${code}_${Date.now().toString()}`},
            }));
            return true;
        },
        [codeLength],
    );

    const _formingUserPhoneData = useCallback(() => {
        if (!phoneVerificationState.phone) return null;

        const data = getUserPhoneDataDecodeParams(userInfo);

        let result: PhoneVerificationUserInfo;
        if (phoneVerificationState.phone.hash) {
            result = {...data, phone_hash: phoneVerificationState.phone.hash};
        } else {
            result = {...data, phone: phoneVerificationState.phone.value};
        }

        return result;
    }, [phoneVerificationState, userInfo]);

    //  Единственная функция не завязанная на antifraud таймер, поскольку на самой странице и так таймится
    const sendCode = useCallback(async (): Promise<PhoneVerificationSendCodeResult> => {
        const phoneData = _formingUserPhoneData();
        if (
            !phoneData ||
            (phoneVerificationState.antifraudExperies.send &&
                Date.now() < phoneVerificationState.antifraudExperies.send)
        )
            return {sended: false, phoneIsValid: false};
        if (phoneVerificationState.status === PhoneVerificationStatus.verified)
            return {
                sended: false,
                phoneIsValid: true,
            };

        let body: VerificationPhoneCodeResponse;
        try {
            const response = await sendVerifyCodeRequest(formDataHash, phoneData);
            if (response.status === 400) {
                phoneBannedAnalyticEvent();
                setPhoneVerificationState((prev) => ({
                    ...prev,
                    status: PhoneVerificationStatus.banned,
                }));
                return {sended: true, phoneIsValid: false};
            }
            body = await response.json();
        } catch (err) {
            if (isDev) console.log(err);
            return {sended: false, phoneIsValid: false};
        }

        setPhoneVerificationState((prev) => ({
            ...prev,
            sessionId: body.verification_session_id,
            status: body.status,
            antifraudExperies: {
                ...prev.antifraudExperies,
                send: Date.now() + Math.max(body.retry_delay_in_ms, VERIFICATION_PHONE_SEND_LIMIT_IN_MS),
            },
        }));

        if (body.status === PhoneVerificationStatus.verified) return {sended: true, phoneIsValid: true};
        return {
            sended: true,
            phoneIsValid: false,
        };
    }, [
        phoneVerificationState,
        formDataHash,
        setPhoneVerificationState,
        phoneBannedAnalyticEvent,
        _formingUserPhoneData,
    ]);

    const _checkVerificationStatus = useCallback(async () => {
        try {
            const data = _formingUserPhoneData();
            if (!phoneVerificationState.phone?.value || !data) return;

            if (prevPhoneState && prevPhoneState.phone === phoneVerificationState.phone.value) {
                //  Если пользователь по итогу не поменял телефон
                return setPhoneVerificationState((prev) => ({
                    ...prev,
                    status: prevPhoneState.state,
                }));
            }

            //  Проверка антифрод лимитов
            const timestamp = Date.now();
            if (
                phoneVerificationState.antifraudExperies.verify &&
                phoneVerificationState.antifraudExperies.verify > timestamp
            ) {
                if (antifraudTimers.verify) return;
                const timer = setTimeout(() => {
                    setAntifraudTimers((prev) => ({...prev, verify: null}));
                }, phoneVerificationState.antifraudExperies.verify - timestamp);
                setAntifraudTimers((prev) => ({...prev, verify: timer}));
                return;
            }

            const response = await checkPhoneVerificationRequest(formDataHash, data);

            if (response.status === 400) {
                setPhoneVerificationState((prevState) => ({
                    ...prevState,
                    status: PhoneVerificationStatus.verificationFailed,
                }));
                return;
            } else if (response.status === 500) {
                //  Emergency flow -> GoToNextScreen
                setPhoneVerificationState((prevState) => ({...prevState, status: PhoneVerificationStatus.verified}));
                return;
            }

            const body: PhoneVerificationCheckCode = await response.json();
            setPhoneVerificationState((prevState) => ({
                ...prevState,
                status: body.status,
                antifraudExperies: {
                    ...prevState.antifraudExperies,
                    verify: Date.now() + (body.retry_delay_in_ms ?? 0),
                },
            }));
            if (body.status !== PhoneVerificationStatus.verified) await sendCode();
        } catch (err) {
            if (isDev) console.log(err);
            //  Skip by error
            setPhoneVerificationState((prevState) => ({...prevState, status: PhoneVerificationStatus.verified}));
        }
    }, [
        phoneVerificationState,
        _formingUserPhoneData,
        setPhoneVerificationState,
        formDataHash,
        setAntifraudTimers,
        antifraudTimers,
        prevPhoneState,
        sendCode,
    ]);

    //  Verification
    useEffect(() => {
        _checkVerificationStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneVerificationState.phone]);

    useEffect(() => {
        if (antifraudTimers.verify) return;
        _checkVerificationStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [antifraudTimers.verify]);

    const _attemptVerifyCode = useCallback(async (): Promise<void> => {
        const phoneData = _formingUserPhoneData();
        if (
            phoneVerificationState.status === PhoneVerificationStatus.verified ||
            !phoneVerificationState.code ||
            !phoneVerificationState.sessionId ||
            !phoneVerificationState.phone ||
            !phoneData
        )
            return;

        const timestamp = Date.now();
        if (
            phoneVerificationState.antifraudExperies.attempt &&
            phoneVerificationState.antifraudExperies.attempt > timestamp
        ) {
            if (antifraudTimers.attempt) return;
            const timer = setTimeout(
                () => setAntifraudTimers((prev) => ({...prev, attempt: null})),
                phoneVerificationState.antifraudExperies.attempt - timestamp,
            );
            setAntifraudTimers((prev) => ({...prev, attempt: timer}));
            return;
        }

        try {
            const response = await attemptVerifyCodeRequest(
                formDataHash,
                Object.assign(phoneData, {
                    verification_code: phoneVerificationState.code.value,
                    verification_session_id: phoneVerificationState.sessionId,
                }),
            );

            if (response.status >= 400 && response.status < 500) {
                throw new Error('Bad data');
            } else if (response.status === 500) {
                //  Emergency flow
                setPhoneVerificationState((prev) => ({...prev, status: PhoneVerificationStatus.verified}));
                return;
            }
            const body: PhoneVerificationCheckCode = await response.json();

            setPhoneVerificationState((prev) => ({
                ...prev,
                status: body.status,
                errors: body.status === PhoneVerificationStatus.unverified ? 'Неверный код' : undefined,
                antifraudExperies: {
                    ...prev.antifraudExperies,
                    attempt: Date.now() + body.retry_delay_in_ms,
                },
            }));
        } catch (err) {
            console.error(err);
            return;
        } finally {
            setPhoneVerificationState((prev) => ({
                ...prev,
                attempt: {
                    ...prev.attempt,
                    timestamp: Date.now(),
                },
            }));
        }
    }, [
        phoneVerificationState,
        setPhoneVerificationState,
        antifraudTimers.attempt,
        formDataHash,
        _formingUserPhoneData,
    ]);

    useEffect(() => {
        _attemptVerifyCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneVerificationState.code?.hash]);

    useEffect(() => {
        if (antifraudTimers.attempt) return;
        _attemptVerifyCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [antifraudTimers.attempt]);

    const changePhone = useCallback(() => {
        setPrevPhoneState({
            phone: phoneVerificationState.phone?.value || '',
            state: phoneVerificationState.status,
        });

        setPhoneVerificationState((prev) => ({
            ...prev,
            code: undefined,
            errors: undefined,
            status: PhoneVerificationStatus.unknown,
        }));
    }, [setPhoneVerificationState, phoneVerificationState, setPrevPhoneState]);

    const resetPhone = useCallback(() => {
        setPhoneVerificationState((prev) => ({
            ...prev,
            code: undefined,
            errors: undefined,
            status: PhoneVerificationStatus.unknown,
        }));
    }, [setPhoneVerificationState]);

    return {
        codeLength,
        phoneVerificationState,
        setPhoneForCheckingVerificationStatus,
        sendCode,
        changePhone,
        resetPhone,
        setCodeForVerify,
    };
}

export default usePhoneVerificationController;
